import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearAuthStorage, getTokenExpiryMs, isTokenExpired, markLogoutSignal } from './authservice/session';

export default function useAutoLogout(inactivityLimit = 60 * 60 * 1000) { 
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);

  const handleLogout = useCallback((setSignal = true) => {
    clearAuthStorage();
    if (setSignal) {
      markLogoutSignal();
    }
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      handleLogout();
      return;
    }

    if (isTokenExpired(token)) {
      handleLogout();
      return;
    }

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        handleLogout();
      }, inactivityLimit);
    };

    const resetExpiryTimer = () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }

      const expiryMs = getTokenExpiryMs(token);
      if (!expiryMs) {
        return;
      }

      const delay = Math.max(expiryMs - Date.now(), 0);
      expiryTimerRef.current = setTimeout(() => {
        handleLogout();
      }, delay);
    };

    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];

    activityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    resetInactivityTimer(); 
    resetExpiryTimer();

    const onStorageChange = (e) => {
      if (e.key === 'logoutSignal') {
        handleLogout(false);
      }
    };
    window.addEventListener('storage', onStorageChange);

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      window.removeEventListener('storage', onStorageChange);
    };
  }, [handleLogout, inactivityLimit]);
}
